import React, { useState } from 'react';
import { Card, Button, Modal, Form } from 'react-bootstrap';
import { 
  BoxArrowUp, 
  Clipboard, 
  ClipboardCheck, 
  Heart, 
  ArrowRepeat, 
  ChatDots,
  BoxArrowUpRight,
  InfoCircle
} from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import useCollections, { CollectionModal } from '../../hooks/useCollections';
import openCapacitorSite from '../../utils/browserUtils';
import './SocialFeedItem.css';

/**
 * SocialFeedItem component displays a single social media post from the RSS feed
 * 
 * @param {Object} props
 * @param {Object} props.item - Feed item data from RSS
 * @param {string} props.variant - Style variant: "default" or "compact"
 */
const SocialFeedItem = ({
  item,
  variant = "default"
}) => {
  const { t } = useTranslation('common');

  // Share functionality states
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Use our collection hook for collection functionality
  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    openCollectionModal,
    closeModal,
    handleSave,
    isStorageAvailable,
    CollectionButton
  } = useCollections();

  // Create share link
  const shareLink = item.url;

  // Format the post content - truncate if too long
  const postContent = truncateText(item.description, 280);
  
  // Format the date for display
  // Note: relative date abbreviations (now/5m/2h/3d) stay English — universal short forms
  const formattedDate = formatRelativeDate(item.created_at);

  // Handle clicking the post to go to source
  const handlePostClick = () => {
    openCapacitorSite(item.url, '_blank');
  };

  // Handle native sharing
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('common:creatorOnGovSky', '{{creator}} on GovSky', { creator: item.creator }),
          text: item.description,
          url: shareLink,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      handleCopyLink();
    }
  };

  // Handle copy link to clipboard
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch((error) => console.error('Error copying link:', error));
  };

  // Prepare the collection item data
  // feedName/branch/topic stay English per stored-data convention
  const collectionItem = {
    title: `${item.creator}: ${truncateText(item.description, 100)}`,
    url: item.url,
    docLink: null,
    date: item.created_at,
    feedName: 'GovSky Social',
    branch: 'Social',
    topic: 'Officials'
  };

  return (
    <div className={`social-feed-item social-feed-item--${variant}`}>
      <Card className="social-card">
        {/* Header with creator info */}
        <div className="social-header">
          <div className="social-avatar">
            {item.creator?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="social-creator-info">
            <div className="social-creator-name">{item.creator}</div>
            <div className="social-handle">@{item.handle}</div>
          </div>
          <div className="social-date">{formattedDate}</div>
        </div>

        {/* Post content */}
        <Card.Body 
          className="social-content"
          onClick={handlePostClick}
          style={{ cursor: 'pointer' }}
        >
          {postContent}
        </Card.Body>

        {/* Image if present */}
        {item.imageUrl && (
          <div className="social-image-container" onClick={handlePostClick}>
            <img 
              src={item.imageUrl} 
              alt={t('common:postAttachmentAlt', 'Post attachment')}
              className="social-image"
              loading="lazy"
            />
          </div>
        )}

        {/* Engagement stats */}
        <div className="social-engagement">
          <span className="engagement-stat">
            <ChatDots /> {item.engagement?.comments || 0}
          </span>
          <span className="engagement-stat">
            <ArrowRepeat /> {item.engagement?.reposts || 0}
          </span>
          <span className="engagement-stat">
            <Heart /> {item.engagement?.likes || 0}
          </span>
        </div>

        {/* Action buttons */}
        <Card.Footer className="d-flex">
          <Button 
            className="clickable me-2" 
            id="billLink"
            onClick={handlePostClick}
            title={t('common:viewOnGovSky', 'View on GovSky')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>{t('common:view', 'View')}
          </Button>
          
          <CollectionButton 
            item={collectionItem} 
            className="me-2"
          />
          
          <Button 
            className="clickable" 
            id="shareButton"
            onClick={handleNativeShare}
            title={t('common:share', 'Share')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-send-horizontal-icon lucide-send-horizontal"><path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"/><path d="M6 12h16"/></svg>
          </Button>
        </Card.Footer>
      </Card>
      
      {/* Share Modal */}
      <Modal 
        id="fullBillModal" 
        show={shareModalOpen} 
        size="xl" 
        centered 
        onHide={() => setShareModalOpen(false)}
      >
        <Modal.Header id="billModalHeader" closeButton>
          <h3>{t('common:sharePost', 'Share Post')}</h3>
        </Modal.Header>
        <Modal.Body id="descriptionModalBody">
          <div className="mb-3">
            <strong>{item.creator}</strong>
            <p className="mt-2" style={{ fontSize: '14px' }}>{truncateText(item.description, 200)}</p>
          </div>
          <Form.Group id="noteModalForm">
            <Form.Control 
              id="noteModalFeedItem" 
              type="text" 
              readOnly 
              value={shareLink} 
            />
          </Form.Group>
          <Modal.Footer id="shareFooter">
            <Button 
              className="clickable me-2" 
              id="addToCollectionButton" 
              onClick={handleNativeShare}
            >
              <BoxArrowUp />
            </Button>
            <Button 
              className="clickable" 
              id="addToCollectionButton" 
              onClick={handleCopyLink}
            >
              {linkCopied ? <ClipboardCheck /> : <Clipboard />}
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>
      
      {/* Collection Modal */}
      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />
    </div>
  );
};

// Helper functions
function truncateText(text, maxLength = 280) {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
}

function formatRelativeDate(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    
    // For older posts, show the date
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  } catch (error) {
    return '';
  }
}

export default SocialFeedItem;